/**
 * RadioGroupField component
 */

// Library
import {
	ChangeEvent,
	DetailedHTMLProps,
	InputHTMLAttributes,
	useEffect,
	useId,
	useMemo,
	useState,
} from 'react';
import { Text } from '@components';
import { WarningQld } from '@public';
import { v4 as uuidv4 } from 'uuid';

// CSS Style for this module
import styles from './radioGroupField.module.scss';

type HTMLInputElementValue = string | number | boolean;

type AccessibilityProps = Pick<
	DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
	'id' | 'name' | 'required'
>;

interface RadioOption {
	label: React.ReactNode | string;
	value: HTMLInputElementValue;
}

/**
 * Interface for RadioGroupField props
 */
interface RadioGroupFieldProps {
	/**
	 * Custom styles of the button
	 */
	customStyles?: string;
	/**
	 * Custom styles of the container
	 */
	customContainerStyles?: string;
	/**
	 * Value and label pairs as options of the radio group
	 */
	options: RadioOption[];
	/**
	 * Name of the field group within a form
	 */
	fieldName: string;
	/**
	 * The default chosen option of the radio group
	 */
	defaultValue?: HTMLInputElementValue;
	/**
	 * onChange function for keeping track field value
	 */
	onChange?: (value: HTMLInputElementValue) => void;
	/**
	 * Helper text for additional guidance about how the input field will be used.
	 * It should only take up a single line
	 */
	hintText?: string;
	/**
	 * Msg to be displayed when the status is error
	 */
	errorMsg?: string;
	/**
	 * Boolean for determining whether this is a disabled field
	 */
	disabled?: boolean;
	/**
	 * Additional props to be passed to the underlying input element for accessibility purposes.
	 * @see https://reactjs.org/docs/accessibility.html#programmatic-access-to-event-handlers
	 */
	accessibilityProps?: AccessibilityProps;
	/**
	 * Boolean for determining whether this field has to be autofocused
	 */
	autoFocus?: boolean;
	/** Element identifier selector for test */
	testId?: string;
}

/**
 * Radio field
 * @param {RadioGroupFieldProps} Prop
 * @returns an styled radio input group element
 */
export const RadioGroupField = ({
	customStyles,
	customContainerStyles,
	fieldName,
	options,
	defaultValue: defaultOption,
	onChange,
	hintText,
	errorMsg,
	disabled = false,
	accessibilityProps,
	autoFocus = false,
	testId,
}: RadioGroupFieldProps) => {
	const [value, setValue] = useState(defaultOption);
	const hasError = useMemo(() => Boolean(errorMsg), [errorMsg]);
	const [optionsIds] = useState(options.map(() => uuidv4()));
	const internalId = useId();
	const [hintId] = useState(`${internalId}-hint`);
	const [errorId] = useState(`${internalId}-error`);

	useEffect(() => {
		setValue(defaultOption);
	}, [defaultOption]);

	const handleRadioChange = (event: ChangeEvent<HTMLInputElement>) => {
		let transformedValue: HTMLInputElementValue = event.target.value;
		if (transformedValue === 'true') {
			transformedValue = true;
		} else if (transformedValue === 'false') {
			transformedValue = false;
		}

		setValue(transformedValue);
		if (onChange) {
			onChange(transformedValue);
		}
	};

	const optionValueAdapter = (option: RadioOption) => {
		return typeof option.value == 'boolean' ? option.value?.toString() : option.value;
	};

	return (
		<div className={[styles.container, customStyles].join(' ')}>
			<div className={styles.textContainer}>
				{hintText && (
					<div>
						<Text variant="muted" id={hintId}>
							{hintText}
						</Text>
					</div>
				)}
				{hasError && (
					<div className={styles.errorContainer}>
						<Text variant="error" id={errorId} role="alert" className={styles.errorContent}>
							<WarningQld customStyles={styles.errorIcon} /> {errorMsg}
						</Text>
					</div>
				)}
			</div>
			<div
				className={[styles['radio-group'], customContainerStyles].join(' ')}
				id={accessibilityProps?.id}
				role="input"
			>
				{options.map((option, index) => {
					const id = optionsIds[index];
					return (
						<div key={id} className={styles['radio-container']}>
							<input
								type="radio"
								value={optionValueAdapter(option)}
								checked={option.value === value}
								onChange={handleRadioChange}
								disabled={disabled}
								className={[styles.field, hasError && styles.error].join(' ')}
								role="radio"
								name={fieldName}
								id={id}
								// Props for accessibility
								required={accessibilityProps?.required}
								aria-label={
									option.label && typeof option.label === 'string' && option.label.length
										? option.label
										: undefined
								}
								aria-describedby={hintId || errorId ? [hintId, errorId].join(' ') : undefined}
								autoFocus={autoFocus && (value !== undefined ? option.value === value : index == 0)}
								// Testing
								data-testid={testId ? `${testId}-${option.value}` : undefined}
							/>
							<label
								className={[styles.label, disabled && styles['label-disabled']].join(' ')}
								htmlFor={id}
							>
								{option.label}
							</label>
						</div>
					);
				})}
			</div>
		</div>
	);
};
